'use client';

/**
 * useNetworkStatus Hook
 *
 * Provides real-time network connectivity status for PWA offline support.
 * Tracks online/offline state and provides information about the connection.
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface NetworkStatus {
  /**
   * Whether the browser is currently online
   */
  isOnline: boolean;

  /**
   * Whether the browser has been offline since mount
   */
  wasOffline: boolean;

  /**
   * Effective connection type (4g, 3g, 2g, slow-2g)
   * Only available in browsers that support Network Information API
   */
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';

  /**
   * Whether data saver mode is enabled
   * Only available in browsers that support Network Information API
   */
  saveData?: boolean;

  /**
   * Estimated round trip time in milliseconds
   * Only available in browsers that support Network Information API
   */
  rtt?: number;

  /**
   * Estimated downlink speed in Mbps
   * Only available in browsers that support Network Information API
   */
  downlink?: number;
}

export interface UseNetworkStatusReturn extends NetworkStatus {
  /**
   * Force a re-check of the network status
   */
  checkConnection: () => void;
}

// ============================================================================
// Network Information API Types
// ============================================================================

interface NetworkInformation {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  saveData?: boolean;
  rtt?: number;
  downlink?: number;
  addEventListener: (type: string, listener: EventListener) => void;
  removeEventListener: (type: string, listener: EventListener) => void;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

// ============================================================================
// Store for useSyncExternalStore
// ============================================================================

const getConnection = (): NetworkInformation | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection;
};

const subscribeToOnlineStatus = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);

  const connection = getConnection();
  if (connection) {
    connection.addEventListener('change', callback);
  }

  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);

    const conn = getConnection();
    if (conn) {
      conn.removeEventListener('change', callback);
    }
  };
};

const getOnlineSnapshot = (): boolean => {
  if (typeof navigator === 'undefined') return true;

  // Workaround: navigator.onLine can incorrectly return false on localhost
  // Only apply this in actual browser development, not during tests
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Vitest check across CJS/ESM
    !(globalThis as any).__vitest_worker__
  ) {
    return true;
  }

  return navigator.onLine;
};

const getServerSnapshot = (): boolean => {
  return true; // SSR always assumes online
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to monitor network connectivity status
 *
 * @example
 * ```tsx
 * const { isOnline, wasOffline, effectiveType } = useNetworkStatus();
 *
 * if (!isOnline) {
 *   return <OfflineIndicator />;
 * }
 * ```
 */
export function useNetworkStatus(): UseNetworkStatusReturn {
  const isOnline = useSyncExternalStore(subscribeToOnlineStatus, getOnlineSnapshot, getServerSnapshot);

  const [wasOffline, setWasOffline] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<Partial<NetworkStatus>>({});

  // Track if we've ever been offline
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    }
  }, [isOnline]);

  // Get connection information
  const updateConnectionInfo = useCallback(() => {
    const connection = getConnection();
    if (connection) {
      setConnectionInfo({
        effectiveType: connection.effectiveType,
        saveData: connection.saveData,
        rtt: connection.rtt,
        downlink: connection.downlink,
      });
    }
  }, []);

  // Subscribe to connection changes
  useEffect(() => {
    updateConnectionInfo();

    const connection = getConnection();
    if (connection) {
      connection.addEventListener('change', updateConnectionInfo);
      return () => {
        connection.removeEventListener('change', updateConnectionInfo);
      };
    }
    return undefined;
  }, [updateConnectionInfo]);

  const checkConnection = useCallback(() => {
    updateConnectionInfo();
  }, [updateConnectionInfo]);

  return {
    isOnline,
    wasOffline,
    ...connectionInfo,
    checkConnection,
  };
}

export default useNetworkStatus;
